/**
 * One computation behind the redesigned dashboard (s80): the stage strip, the
 * Stage-3 next-up pointer, and the category progress grid all read from here so
 * the section-overview + entries are fetched once and `computeNextSection` /
 * `summarizeCategoryProgress` run once. TanStack dedupes the queries by key.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Client } from "@shared/schema";
import type { SectionOverviewItem } from "@shared/section-overview";
import { sectionProgress, type CompletionEntry, type CompletionField } from "@/lib/section-completion";
import { SECTION_SLUGS } from "@shared/constants/sections";
import { useEstateReadyScore } from "./useEstateReadyScore";
import { useExecutorRemovedRows } from "@/lib/executor-plan-edit";
import { useAuth } from "@/contexts/AuthContext";
import {
  computeNextSection,
  summarizeCategoryProgress,
  type NextSection,
  type CategoryProgress,
  type SituationFlags,
} from "@/lib/next-section";

function flagsOf(c: Client): SituationFlags {
  return {
    hasChildren: c.hasChildren,
    hasDependents: c.hasDependents,
    hasPets: c.hasPets,
    ownsProperty: c.ownsProperty,
    ownsBusiness: c.ownsBusiness,
  };
}

export interface DashboardProgress {
  isLoading: boolean;
  /** Applicable essentials done / total (N/A-excluded). Mirrors the essentials card. */
  score: number;
  total: number;
  /** About You section complete — Stage 1's done predicate. (The standalone identity ERS
   *  essential was removed in s86; this now reads the section directly.) */
  identityDone: boolean;
  /** Stage 1's counter, from the SAME `sectionProgress` call behind `identityDone`
   *  (s187, A4 Build 2a). `filled` / `total` required About You fields as the shared math
   *  measures them at runtime — never a constant. Both are 0 when the section is absent
   *  or unmeasurable (`sectionProgress` returns null), which the strip renders as prose
   *  rather than "0 of 0", matching how Stage 3 handles `sectionsTotal === 0`. */
  identityFilled: number;
  identityTotal: number;
  /** All applicable essentials in place (the Talk-to-Executor + Stage-3-primary gate). */
  essentialsComplete: boolean;
  /** The single highest-priority incomplete thing, or null when nothing remains.
   *  With `deferredSlugs`, deferred items are skipped (F86-8b). */
  next: NextSection | null;
  /** F86-8b: true when there WAS a next item but every remaining outstanding thing has
   *  been deferred this session — the card shows a "come back later" message, not a
   *  pointer. Distinct from `next === null` (genuinely nothing left). */
  allDeferred: boolean;
  /**
   * 🔴 THE plan-complete predicate — READ FROM THE SERVER, never derived here.
   * s185 (D-185.2): this used to be `!allDeferred && next === null`, a second
   * derivation that could disagree with the server's answer. The server computes it
   * over the bar's whole universe (`GET /api/wizard/progress`, `client-routes.ts`:
   * every scored section complete), which is the every-section claim the banner copy
   * makes. FOURTH-PLANCOMPLETE (D-179.19) closes with this: the value already
   * reflects `recordTotal`/`withheldTotal` upstream, so `NextUpCard` no longer needs
   * a lens reference to be honest about them.
   *
   * 🔴 Do NOT re-derive it — not from `next === null`, not from
   * `sectionsComplete === sectionsTotal`. Both are different predicates off the same
   * shared math, and re-deriving is exactly what D-185.2 retired.
   *
   * 🔴 Session deferrals no longer affect this (they were the `!allDeferred` half).
   * That is the ruled state; where the deferral concept lands permanently is the
   * post-A4 ruling chartered in `deferred-builds.md` § New & amended entries (s185).
   * `allDeferred` still ships below — `NextUpCard` renders its own "come back later"
   * state from it, which is presentation, not a completion claim.
   */
  planComplete: boolean;
  categoryProgress: CategoryProgress[];
  sectionsComplete: number;
  sectionsTotal: number;
}

/** Stable empty default so callers that don't defer keep referential stability. */
const EMPTY_DEFER: ReadonlySet<string> = new Set<string>();

/** The slug a nextStep/section is deferred by: the `?section=` slug in the deep-link,
 *  else the deep-link itself (covers the one essential — key documents — with no
 *  section surface). Mirrors NextUpCard's `deferId` so the two agree. */
const deferKeyOf = (deepLink: string): string =>
  deepLink.match(/[?&]section=([^&]+)/)?.[1] ?? deepLink;

export function useDashboardProgress(
  clientData: Client | null,
  deferredSlugs: ReadonlySet<string> = EMPTY_DEFER,
): DashboardProgress {
  const clientId = clientData?.id;
  const { user } = useAuth();

  const { data: ers } = useEstateReadyScore(clientId);

  const { data: overview } = useQuery<SectionOverviewItem[]>({
    queryKey: ["/api/clients", clientId, "section-overview"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/section-overview`);
      if (!res.ok) throw new Error("Failed to load section overview");
      return res.json();
    },
    enabled: !!clientId,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const { data: entries } = useQuery<CompletionEntry[]>({
    queryKey: ["/api/clients", clientId, "generic-entries", "all"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/generic-entries`);
      if (!res.ok) throw new Error("Failed to load entries");
      return res.json();
    },
    enabled: !!clientId,
    staleTime: 0,
    refetchOnMount: "always",
  });

  // 🔴 s185 (D-185.2): the plan-complete answer is the SERVER's. Same query key as
  // `Dashboard` / `WizardProgressCard`, so TanStack dedupes the request rather than
  // issuing a second one. `enabled` is deliberate: the route is session-derived and
  // 403s any non-client role, and a 403 THROWS through `getQueryFn` — an un-gated
  // throw out of a shared hook is the F-S170.1 shape that took the executor tree down.
  const { data: wizard } = useQuery<{ planComplete?: boolean }>({
    queryKey: ["/api/wizard/progress"],
    enabled: !!clientId && user?.role === "client",
    staleTime: 0,
    refetchOnMount: "always",
  });

  // 🔴 s185 (INPUT-ASSEMBLY-1): rows the EXECUTOR soft-deleted. The SERVER adapter has filtered
  // these since s145 (`buildSectionProgressInputs` skips them); this hook never read them, so the
  // dashboard rollup scored rows the section pill and the server both ignored. `complete@` read
  // "25 of 26" with every section showing a green check, and a hard refresh could not clear it
  // because nothing was stale — the two adapters were handed different ROWS.
  //
  // 🔴 Via the SHARED hook, not a second fetch. The first cut of this hand-rolled its own query and
  // typed it `useQuery<string[]>`; the endpoint actually ships `{ deletedRowIds }`, so `new Set(...)`
  // received an object, threw "object is not iterable", and took the whole dashboard to the error
  // boundary. The generic was a compile-time claim about a runtime shape and checked nothing.
  // `useExecutorRemovedRows` already owned that unwrapping (G7) — reusing it is the fix.
  const removedRowIds = useExecutorRemovedRows(clientId, !!clientId);

  // Manually-N/A'd sections (s85) drop out of the pointer walk + progress grid.
  const { data: naSlugs } = useQuery<string[]>({
    queryKey: ["/api/clients", clientId, "section-na"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/section-na`);
      if (!res.ok) throw new Error("Failed to load N/A sections");
      return res.json();
    },
    enabled: !!clientId,
    staleTime: 0,
    refetchOnMount: "always",
  });

  return useMemo<DashboardProgress>(() => {
    const flags = clientData ? flagsOf(clientData) : {};
    const sections = overview ?? [];
    const ent = entries ?? [];
    const manualNa = new Set(naSlugs ?? []);
    const score = ers?.score ?? 0;
    const total = ers?.total ?? 0;
    // Stage-1's done predicate. The identity ERS essential was removed (s86); anchor
    // directly on the surviving About You section-completion green-check — the same
    // signal that essential wrapped. Mirrors next-section.ts's sectionComplete().
    //
    // 🔴 s187 (A4 Build 2a): this called `isSectionComplete`, which is a thin wrapper that
    // computes `{complete, filled, total}` and then THROWS THE COUNTS AWAY. Stage 1 was the
    // only stage card with no counter for exactly that reason — the numbers were computed
    // on every render and discarded one layer below. Calling `sectionProgress` directly
    // keeps them. This is the SAME computation, not a second one: `isSectionComplete` is
    // literally `sectionProgress(...)?.complete ?? false`.
    //
    // 🔴 The stale comment this replaces claimed "≥5/7". That threshold was RETIRED at s110
    // (D-110.3) — About You completes only when every required field is filled-or-declined
    // (`filled === total`), which `field-display-rules.md` §3 records correctly (`completion-model.md` retired s191). The comment was
    // four sessions of drift; the code was always right. Do not reintroduce a figure here —
    // the denominator is whatever the shared math returns at runtime.
    const aboutYou = sections.find((s) => s.sectionSlug === SECTION_SLUGS.ABOUT_YOU);
    const identity = aboutYou
      ? sectionProgress(
          { slug: aboutYou.sectionSlug, layoutType: aboutYou.layoutType ?? undefined, fields: aboutYou.fields as CompletionField[], declaresEmptyStarter: aboutYou.declaresEmptyStarter },
          ent,
        )
      : null;
    const identityDone = identity?.complete ?? false;
    // F86-8b "Come back later": session-deferred items are skipped here. For essentials
    // we filter the (ERS) nextSteps; for the post-essentials section walk we merge the
    // deferred slugs into the skip set (computeNextSection already skips that set).
    const rawSteps = ers?.nextSteps ?? [];
    const filteredSteps = deferredSlugs.size
      ? rawSteps.filter((s) => !deferredSlugs.has(deferKeyOf(s.deepLink)))
      : rawSteps;
    let skipSet: ReadonlySet<string> = manualNa;
    if (deferredSlugs.size) {
      const merged = new Set(manualNa);
      deferredSlugs.forEach((s) => merged.add(s));
      skipSet = merged;
    }
    // All outstanding ESSENTIALS deferred → show the deferred message, don't fall through
    // to sections (Phase 3). Otherwise compute next over the filtered/ skip-merged inputs.
    const allEssentialsDeferred = rawSteps.length > 0 && filteredSteps.length === 0;
    let next: NextSection | null;
    let allDeferred: boolean;
    if (allEssentialsDeferred) {
      next = null;
      allDeferred = true;
    } else {
      next = computeNextSection(sections, ent, flags, filteredSteps, skipSet, removedRowIds);
      // Essentials done but every remaining SECTION deferred → also the deferred state.
      allDeferred =
        next === null &&
        deferredSlugs.size > 0 &&
        computeNextSection(sections, ent, flags, rawSteps, manualNa, removedRowIds) !== null;
    }
    const categoryProgress = summarizeCategoryProgress(sections, ent, flags, manualNa, removedRowIds);
    return {
      isLoading: !ers || !overview || !entries,
      score,
      total,
      identityDone,
      identityFilled: identity?.filled ?? 0,
      identityTotal: identity?.total ?? 0,
      essentialsComplete: total > 0 && score === total,
      next,
      allDeferred,
      // s185 (D-185.2): READ, never derived. Absent payload (still loading, or the
      // query is disabled for a non-client viewer) is NOT a completion claim.
      planComplete: wizard?.planComplete === true,
      categoryProgress,
      sectionsComplete: categoryProgress.reduce((n, c) => n + c.complete, 0),
      sectionsTotal: categoryProgress.reduce((n, c) => n + c.total, 0),
    };
  }, [clientData, ers, overview, entries, naSlugs, deferredSlugs, wizard, removedRowIds]);
}
